// 覚醒王の壁跳ね・往 (feralWallDash・配布版の新版) を千秋楽で直す比較版 (2026-10-09・DEV ?wallDashFix=pounce|late|dmg)。
//   記録と測定 = docs/research/2026-10-09-awoken-wall-dash-fix-options.md。
//   既定 = 案1 pounce (2026-10-10 ユーザー採用「案1だけ」)・旧は DEV ?wallDashFix=off。
//   配布版は URL を読まない (main.ts の DEV の箱だけが set する)。
//
// 何が起きているか (確認済み・同じ文書):
//   往は「溜め 720ms → 踏み切り」。溜めの低い構えの絵は、もう王の中心から約 216px 先まで描かれている。
//   それより内に立っていれば、踏み切った最初のコマで当たる (溜めのリズムで回避すれば抜けられる)。
//   約 225px より外に立つと、踏み切りでは当たらない。襲う絵のまま 130ms ほど宙に留まってから走り出し、147〜560ms 後に当たる。
//   溜めの間に少し下がった人は「踏み切りに合わせて回避 → 無敵が切れた後に当たる」になる (人の記録 15回中 5回)。
//   一番近い所 (踏み切り +147〜226ms) は、見てから押すには短い。
//
// 案 (どれも拍数・溜めの長さ・壁に着く時刻・絵の順番は変えない):
//   pounce = 踏み切りで跳びかかる。踏み切った瞬間に主人公との間を見て、襲う絵のまま最大 100px を 90ms で詰める
//            (今は宙に留まっている間・部屋の中ほどからだと少し後ろへ戻っている間)。中心から約 300px までに立つ人は、
//            踏み切りから 80ms 以内に当たる = 答えが「踏み切りに合わせて回避」の1つになる。それより遠い人は今と同じく走って来るのを見て避ける。
//   late   = 踏み切りで届かない時は、見てから間に合う所で当たる。踏み切りの後、宙に留まる間を 120ms 延ばし、残りを少し速く走る
//            (壁に着く時刻は同じ)。踏み切りで当たらなかった人には、踏み切りから約 245ms より前に当たらない。
//   dmg    = 傷を軽くする (往だけ・体力 18→12・体幹 36→24)。読み方は今のまま。台帳の「理不尽ならダメージ側で」。
import type { AttackDef } from './types'

export type WallDashFix = 'now' | 'pounce' | 'late' | 'dmg'
let mode: WallDashFix = 'pounce'

export function parseWallDashFix(raw: string | null | undefined): WallDashFix {
  if (raw === '0' || raw === 'off' || raw === 'now') return 'now'
  return raw === 'late' || raw === 'dmg' ? raw : 'pounce'
}
/** DEV の URL の値を渡す。指定なし・知らない値 = pounce、'0' / 'off' / 'now' = 旧。 */
export function setWallDashFix(raw: string | null | undefined): void {
  mode = parseWallDashFix(raw)
}
export function wallDashFix(): WallDashFix {
  return mode
}

// ---- pounce: 踏み切りで跳びかかる ----
/** この間合い (王の中心から主人公の中心) までは、今でも踏み切りの最初のコマで当たる (実測 220〜225px の少し内)。 */
export const WALL_DASH_POUNCE_REACH_PX = 205
export const WALL_DASH_POUNCE_MAX_PX = 100
export const WALL_DASH_POUNCE_MS = 90

/** 跳びかかりの進み (0→1)。最初に速く、詰め終わりで減速する。 */
export function wallDashPounceEase(elapsedMs: number): number {
  const t = Math.max(0, Math.min(1, elapsedMs / WALL_DASH_POUNCE_MS))
  return 1 - (1 - t) * (1 - t)
}

// 跳びかかりの位置の計算と毎 tick の更新は src/awoken_wall_hunt_game.ts (wallHuntPounceStartX / pounceWallHunt)。

// ---- late: 届かない時は見てから間に合う所で ----
/** 踏み切りの後、宙に留まる間を延ばす長さ (ms)。この間は踏み切りの最初のコマのまま。 */
export const WALL_DASH_LATE_HOLD_MS = 120
/** 往の走り (active) の経過 ms → 今の版の経過 ms。最初を溜めて、残りを少し速く走る。壁に着く時刻 (activeMs) は同じ。 */
export function wallDashLateMs(ms: number, activeMs: number): number {
  if (wallDashFix() !== 'late') return ms
  const hold = WALL_DASH_LATE_HOLD_MS
  if (ms <= hold) return 0
  return Math.min(activeMs, (ms - hold) * activeMs / (activeMs - hold))
}

// ---- dmg: 傷を軽くする ----
export const WALL_DASH_DMG = { damage: 12, postureDamage: 24 } as const
export function wallDashFixAttack(atk: AttackDef): AttackDef {
  if (wallDashFix() !== 'dmg' || atk.id !== 'feralWallDash') return atk
  return { ...atk, ...WALL_DASH_DMG }
}
